import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Modal, ModalHeader, ModalBody, Button, Spinner, Table } from "reactstrap";
import { ETAPAS_VIAJE, sinFecha, muestraFecha } from "./contenedorUtils";

const auth = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });

// Checklist de seguimiento: bandera del documento y fecha en que quedó listo
const CHECKLIST = [
  { flag: "bl", fecha: "fechaBl", label: "BL" },
  { flag: "packing", fecha: "fechaPl", label: "Packing list" },
  { flag: "invoice", fecha: "fechaFactura", label: "Factura" },
  { flag: "telex", fecha: "fechaTelex", label: "Telex / SWB" },
  { flag: "BLRevalidado", fecha: "fechaRevalidacion", label: "Revalidación" },
  { flag: "EIR", fecha: "fechaEIR", label: "EIR" },
  { flag: "toquePiso", fecha: "fechaToquePiso", label: "Toque de piso", auto: true },
];

// Fecha de cada etapa según lo que reporta ShipsGo
function fechaEtapa(c, etapa) {
  const f = (x) => muestraFecha(x);
  switch (etapa) {
    case "Espacio confirmado": return f(c.emptyToShipperDate);
    case "Cargado en origen": return f(c.gateInDate);
    case "En puerto de origen": return f(c.gateInDate);
    case "Zarpó": return f(c.departureDate);
    case "Transbordo": return c.transbordos && c.transbordos[0] ? f(c.transbordos[0].llegada) : "";
    case "Arribó": return f(c.arrivalDate) || f(c.dischargeDate);
    default: return "";
  }
}

/**
 * Rastreo y seguimiento del contenedor: etapas del viaje (ShipsGo), ETA y su historial, transbordos,
 * checklist de documentos y el historial de cambios (quién, cuándo, antes y después).
 *  props: isOpen, toggle, contenedorId, onActualizado(contenedor)
 */
export default function ContenedorRastreo({ isOpen, toggle, contenedorId, onActualizado }) {
  const [c, setC] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [sincronizando, setSincronizando] = useState(false);
  const [verTodo, setVerTodo] = useState(false);

  const carga = () => {
    setC(null);
    Promise.all([
      axios.get(`${process.env.REACT_APP_URL_CONTENEDORES}/${contenedorId}`, { headers: auth() }),
      axios.get(`${process.env.REACT_APP_URL_CONTENEDORES_HISTORIAL}/${contenedorId}`, { headers: auth() }),
    ])
      .then(([a, b]) => {
        setC(a.data);
        setHistorial(Array.isArray(b.data) ? b.data : []);
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "No se pudo cargar", text: err?.response?.data?.message || err.message });
        toggle();
      });
  };

  useEffect(() => {
    if (isOpen && contenedorId) {
      setVerTodo(false);
      carga();
    }
  }, [isOpen, contenedorId]); // eslint-disable-line

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_URL_CONTENEDORES_SINCRONIZAR}/${contenedorId}`, {}, { headers: auth() });
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: data.mensaje, showConfirmButton: false, timer: 2000 });
      carga();
      onActualizado && onActualizado(data.contenedor);
    } catch (err) {
      Swal.fire({ icon: "warning", title: "ShipsGo", text: err?.response?.data?.mensaje || err?.response?.data?.message || err.message });
    }
    setSincronizando(false);
  };

  const etapaIdx = c ? ETAPAS_VIAJE.indexOf(c.etapaViaje) : -1;
  const sinTransbordo = c && !(c.transbordos || []).length;
  const cambiosEta = historial.filter((h) => h.campo === "etaReal");
  const historialVisible = verTodo ? historial : historial.slice(0, 15);
  const cliente = c && c.clientes && c.clientes[0] ? c.clientes[0].nombre_comercial : "";
  const naviera = c && c.navieras && c.navieras[0] ? c.navieras[0].name : "";

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle} className="rv-modal">
      <ModalHeader toggle={toggle}>
        <span className="rv-modal-title">
          <i className="fas fa-route" /> Rastreo y seguimiento · <strong>{c ? c.referencia : "…"}</strong>
          {c && c.numeroReferencia ? <small>{c.numeroReferencia}</small> : null}
        </span>
      </ModalHeader>
      <ModalBody>
        {!c ? (
          <div className="rc-state"><Spinner /> <div>Cargando…</div></div>
        ) : (
          <>
            <div className="rv-resumen">
              <div className="rv-dato"><span>Cliente</span><strong>{cliente || "—"}</strong></div>
              <div className="rv-dato"><span>Naviera</span><strong>{naviera || "—"}</strong></div>
              <div className="rv-dato"><span>Buque</span><strong>{!sinFecha(c.buque) ? c.buque : !sinFecha(c.vessel) ? c.vessel : "—"}</strong></div>
              <div className="rv-dato"><span>Tipo</span><strong>{c.tipoContenedor || "—"}</strong></div>
              <div className="rv-dato"><span>Origen → Destino</span><strong>{[c.puerto, c.puertoDestino].filter((x) => x && x !== "NA").join(" → ") || "—"}</strong></div>
              <div className="rv-dato">
                <span>ShipsGo</span>
                <strong>
                  {c.rastreo === "Si" ? <span className="badge-soft badge-soft-success">Con rastreo</span> : <span className="badge-soft badge-soft-warning">Sin rastreo</span>}
                </strong>
              </div>
            </div>

            <div className="cr-acciones">
              {c.rastreo === "Si" ? (
                <Button size="sm" color="primary" onClick={sincronizar} disabled={sincronizando}>
                  {sincronizando ? <Spinner size="sm" /> : <i className="fas fa-sync-alt" />} Actualizar rastreo
                </Button>
              ) : null}
              {c.mapa && c.mapa !== "No" ? (
                <a className="btn btn-sm btn-info" href={c.mapa} target="_blank" rel="noopener noreferrer">
                  <i className="fas fa-map-marked-alt" /> Ver mapa
                </a>
              ) : null}
              {c.ultimaSincronizacion ? (
                <span className="td-muted">Última consulta: {moment(c.ultimaSincronizacion).format("DD/MM/YYYY HH:mm")}</span>
              ) : null}
            </div>

            <div className="form-section rv-section">
              <div className="form-section-header">
                <i className="fas fa-ship" /><h4 className="form-section-title">Etapas del viaje</h4>
                <div style={{ marginLeft: "auto" }}>
                  {c.statusSeguimiento ? <span className="badge-soft badge-soft-info">México: {c.statusSeguimiento}</span> : null}
                </div>
              </div>
              <div className="form-section-body">
                <div className="rv-stepper cr-etapas">
                  {ETAPAS_VIAJE.map((e, i) => {
                    const hecho = i <= etapaIdx;
                    const saltado = e === "Transbordo" && sinTransbordo && etapaIdx > i;
                    return (
                      <div key={e} className={`rv-step${hecho && !saltado ? " done" : ""}${saltado ? " skip" : ""}`}>
                        <span className="rv-step-dot">{hecho && !saltado ? <i className="fas fa-check" /> : saltado ? "—" : i + 1}</span>
                        <span className="rv-step-label">{saltado ? "Sin transbordo" : e}</span>
                        <span className="rv-step-date">{fechaEtapa(c, e) || "—"}</span>
                      </div>
                    );
                  })}
                </div>
                {c.rastreo !== "Si" ? (
                  <div className="td-muted" style={{ fontSize: "0.72rem" }}>
                    Las etapas avanzan solas cuando el contenedor tiene rastreo en ShipsGo.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="cr-grid">
              <div className="form-section rv-section">
                <div className="form-section-header"><i className="fas fa-calendar-alt" /><h4 className="form-section-title">ETA</h4></div>
                <div className="form-section-body">
                  <div className="rv-demoras">
                    <div className="rv-dato"><span>ETD</span><strong>{muestraFecha(c.etd) || "—"}</strong></div>
                    <div className="rv-dato"><span>ETA estimada</span><strong>{muestraFecha(c.etaEstimada) || "—"}</strong></div>
                    <div className="rv-dato"><span>ETA actualizada</span><strong>{muestraFecha(c.etaReal) || "—"}</strong></div>
                    <div className="rv-dato"><span>Primera ETA (ShipsGo)</span><strong>{muestraFecha(c.firstEta) || "—"}</strong></div>
                  </div>
                  <div className="cr-subtitulo">Cambios de ETA ({cambiosEta.length})</div>
                  {cambiosEta.length === 0 ? (
                    <div className="td-muted">Sin cambios registrados.</div>
                  ) : (
                    <Table size="sm" className="rv-table">
                      <thead><tr><th>Cuándo</th><th>Anterior</th><th>Nueva</th><th>Origen</th></tr></thead>
                      <tbody>
                        {cambiosEta.map((h) => (
                          <tr key={h._id}>
                            <td>{moment(h.createdAt).format("DD/MM/YY HH:mm")}</td>
                            <td><del>{h.anterior || "—"}</del></td>
                            <td className="td-strong">{h.nuevo || "—"}</td>
                            <td>{h.usuario}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  {(c.transbordos || []).length ? (
                    <>
                      <div className="cr-subtitulo">Transbordos</div>
                      <Table size="sm" className="rv-table">
                        <thead><tr><th>Puerto</th><th>Llegada</th><th>Salida</th><th>Buque</th></tr></thead>
                        <tbody>
                          {c.transbordos.map((tb, i) => (
                            <tr key={i}>
                              <td className="td-strong">{tb.puerto}</td>
                              <td>{tb.llegada || "—"}</td>
                              <td>{tb.salida || "—"}</td>
                              <td>{tb.buque}{tb.viaje ? ` · ${tb.viaje}` : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="form-section rv-section">
                <div className="form-section-header">
                  <i className="fas fa-clipboard-check" /><h4 className="form-section-title">Checklist de seguimiento</h4>
                  <div style={{ marginLeft: "auto" }} className="rv-pasos-txt">
                    {CHECKLIST.filter((d) => c[d.flag] === "Si").length} / {CHECKLIST.length}
                  </div>
                </div>
                <div className="form-section-body">
                  <div className="cr-checklist">
                    {CHECKLIST.map((d) => {
                      const listo = c[d.flag] === "Si";
                      return (
                        <div key={d.flag} className={`cr-check${listo ? " ok" : ""}`}>
                          <i className={listo ? "fas fa-check-circle" : "far fa-circle"} />
                          <span>{d.label}</span>
                          <small>{listo ? muestraFecha(c[d.fecha]) || "Listo" : d.auto ? "Con el arribo (ShipsGo)" : "Pendiente"}</small>
                        </div>
                      );
                    })}
                  </div>
                  <div className="td-muted" style={{ fontSize: "0.7rem", marginTop: 6 }}>
                    Se marcan solos, con fecha, al subir el archivo. El toque de piso se marca con la descarga que reporta ShipsGo.
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section rv-section">
              <div className="form-section-header">
                <i className="fas fa-history" /><h4 className="form-section-title">Historial de cambios ({historial.length})</h4>
              </div>
              <div className="form-section-body">
                {historial.length === 0 ? (
                  <div className="td-muted">Aún no hay cambios registrados (se guardan a partir de esta versión).</div>
                ) : (
                  <>
                    <div className="rv-historial">
                      {historialVisible.map((h) => (
                        <div key={h._id} className="rv-hist">
                          <span className="rv-hist-fecha">{moment(h.createdAt).format("DD/MM/YYYY HH:mm")}</span>
                          <span className="rv-hist-campo">{h.etiqueta || h.campo}</span>
                          <span className="rv-hist-cambio">
                            {h.anterior ? <del>{h.anterior}</del> : null}
                            {h.anterior ? " → " : ""}
                            <b>{h.nuevo || "—"}</b>
                          </span>
                          <span className={`rv-hist-user${h.fuente !== "Usuario" ? " sistema" : ""}`}>{h.usuario}</span>
                        </div>
                      ))}
                    </div>
                    {historial.length > 15 && !verTodo ? (
                      <Button size="sm" color="link" onClick={() => setVerTodo(true)}>Ver los {historial.length} cambios</Button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
}
